import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { getHouseholdIcon } from "../lib/householdIcons";

/**
 * HouseholdSwitchTransition — full-screen blurred overlay shown while we
 * swap the active household. Replaces the awkward "switched, but the page
 * still shows the old data" gap by holding a friendly animation over the
 * top until both:
 *   1. The `/households/switch` API call resolves, AND
 *   2. The new household's containers + feed have been re-fetched.
 *
 * Visual:
 *   • Backdrop = the underlying page blurred + dimmed (so the user clearly
 *     understands "the app is loading new context").
 *   • A pair of house icons sit side-by-side with an arrow between them.
 *     The LEFT house represents the household they're leaving (faded out).
 *     The RIGHT house "fills up" with QFresh teal as it animates in — a
 *     progress beat that tells the user "your new fridge is loading".
 *   • Brand-coloured copy underneath shows the new household's name so the
 *     transition feels intentional, not a generic loading spinner.
 *
 * Controlled component — `open`, `fromName`, `toName` are owned by the
 * caller. The overlay is "dumb" and just animates; the caller decides
 * when to hide it (typically after data has finished loading).
 */
export const HouseholdSwitchTransition = ({
  open,
  fromName,
  toName,
  fromIcon,
  toIcon,
}) => {
  // Resolve the actual lucide components from the user's chosen slug so the
  // transition reflects each household's identity (e.g. palm tree for the
  // beach house, caravan for the camper). Fallback = generic house.
  const FromIcon = getHouseholdIcon(fromIcon);
  const ToIcon = getHouseholdIcon(toIcon);
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          // z-index sits above every existing sheet/overlay (modals are at
          // 2147483600) so nothing can poke through during the transition.
          className="fixed inset-0 z-[2147483700] flex items-center justify-center"
          data-testid="household-switch-transition"
          aria-live="polite"
          aria-busy="true"
        >
          {/* Backdrop: blurred + tinted with the QFresh cream so it feels
              warm rather than the generic dark dim. */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="absolute inset-0 bg-[#FDFBF7]/85 backdrop-blur-xl"
          />

          {/* Foreground card */}
          <motion.div
            initial={{ scale: 0.94, y: 12, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.94, y: 12, opacity: 0 }}
            transition={{ type: "spring", stiffness: 240, damping: 24 }}
            className="relative z-10 w-[300px] max-w-[90vw] tup-card p-6 text-center"
          >
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#8A93A6]">
              Switching home
            </div>

            {/* House → arrow → House animation */}
            <div className="mt-5 flex items-center justify-center gap-3">
              {/* Old household — fades from full opacity to ghosted as the
                  arrow sweeps right. Empty interior to read as "left". */}
              <motion.div
                initial={{ opacity: 1, scale: 1 }}
                animate={{ opacity: 0.35, scale: 0.92 }}
                transition={{ duration: 0.9, delay: 0.1 }}
                className="w-14 h-14 rounded-2xl border-2 border-[#8A93A6] bg-white flex items-center justify-center"
                data-testid="switch-transition-from"
              >
                <FromIcon className="w-7 h-7 text-[#8A93A6]" />
              </motion.div>

              {/* Animated arrow — slides from the left house toward the
                  right house in lock-step with the fill animation below. */}
              <motion.div
                initial={{ x: -8, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.25 }}
              >
                <ArrowRight className="w-5 h-5 text-[#277A82]" />
              </motion.div>

              {/* New household — starts as an empty outlined house, then a
                  teal "fill" rises from the bottom over ~0.9s before
                  settling. We use an overflow-clipped wrapper so the fill
                  animation can `scaleY` from the bottom edge without
                  bleeding outside the rounded corners. */}
              <div
                className="relative w-14 h-14 rounded-2xl border-2 border-[#277A82] overflow-hidden bg-white"
                data-testid="switch-transition-to"
              >
                <motion.div
                  initial={{ scaleY: 0 }}
                  animate={{ scaleY: 1 }}
                  transition={{ duration: 0.9, delay: 0.4, ease: "easeOut" }}
                  style={{ transformOrigin: "bottom" }}
                  className="absolute inset-0 brand-gradient-bg"
                />
                <ToIcon className="w-7 h-7 text-white absolute inset-0 m-auto z-10" />
              </div>
            </div>

            <div className="mt-5">
              {fromName && (
                <div
                  className="text-xs text-[#8A93A6] truncate"
                  data-testid="switch-transition-from-name"
                >
                  Leaving <span className="font-semibold text-[#4F5D75]">{fromName}</span>
                </div>
              )}
              <div
                className="mt-1 text-base font-heading font-semibold text-[#1F5566] truncate"
                data-testid="switch-transition-to-name"
              >
                {toName ? `Entering ${toName}` : "Loading your home"}
              </div>
            </div>

            {/* Subtle progress beat — three dots cycling so the user has a
                visual heartbeat if the data takes a beat longer than the
                primary animation. */}
            <div className="mt-5 flex justify-center gap-1.5">
              {[0, 1, 2].map((i) => (
                <motion.span
                  key={i}
                  className="w-1.5 h-1.5 rounded-full bg-[#277A82]"
                  initial={{ opacity: 0.25 }}
                  animate={{ opacity: [0.25, 1, 0.25] }}
                  transition={{ duration: 1.05, delay: i * 0.18, repeat: Infinity }}
                />
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default HouseholdSwitchTransition;
